/**
 * Tree View Settings Store
 *
 * Phase 3 Slice 8: Ancestor/Descendant Depth Controls
 *
 * Manages user preferences for tree visualization:
 * - Ancestor depth (how many generations upward from root)
 * - Descendant depth (how many generations downward from root)
 *
 * Preferences persist across sessions via localStorage.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export const UNLIMITED_DEPTH = -1; // Sentinel value for "All" option

interface TreeViewSettings {
  // Depth limits
  ancestorDepth: number; // Number of generations upward (-1 = unlimited)
  descendantDepth: number; // Number of generations downward (-1 = unlimited)

  // Actions
  setAncestorDepth: (depth: number) => void;
  setDescendantDepth: (depth: number) => void;
  resetDepths: () => void;
}

// Default depth values for MVP
const DEFAULT_ANCESTOR_DEPTH = 3; // Show 3 generations upward
const DEFAULT_DESCENDANT_DEPTH = 2; // Show 2 generations downward

export const useTreeViewSettings = create<TreeViewSettings>()(
  persist(
    (set) => ({
      // Initial state
      ancestorDepth: DEFAULT_ANCESTOR_DEPTH,
      descendantDepth: DEFAULT_DESCENDANT_DEPTH,

      // Actions
      setAncestorDepth: (depth: number) => set({ ancestorDepth: depth }),
      setDescendantDepth: (depth: number) => set({ descendantDepth: depth }),
      resetDepths: () => set({
        ancestorDepth: DEFAULT_ANCESTOR_DEPTH,
        descendantDepth: DEFAULT_DESCENDANT_DEPTH
      }),
    }),
    {
      name: 'family-tree-view-settings', // localStorage key
    }
  )
);
